import { styled } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { ToggleButtonGroupDesignSystemScope } from '@openx/components-core/lib/ToggleButtonGroupField/ToggleButtonGroupDesignSystemScope.tsx';
import { AllowBlockType } from '@openx/types/targeting/targeting.ts';
import { useTranslation } from 'react-i18next';

export interface AllowBlockToggleProps {
  /**
   * Current toggle state (Allow or Block)
   */
  value: AllowBlockType;

  /**
   * Callback when toggle state changes
   */
  onChange: (value: AllowBlockType) => void;

  /**
   * Whether the entire toggle is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether only the Allow option is disabled (Block stays active)
   * @default false
   */
  disableAllow?: boolean;

  /**
   * Test ID for automated testing
   * @default 'allow-block-toggle'
   */
  dataTest?: string;
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  height: 32,
  '& .MuiToggleButton-root': {
    textTransform: 'uppercase',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    '&.Mui-selected': {
      border: 'none',
    },
  },
}));

const StyledAllowToggleButton = styled(ToggleButton)(({ theme }) => ({
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const StyledBlockToggleButton = styled(ToggleButton)(({ theme }) => ({
  '&.Mui-selected': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

/**
 * Toggle button group for switching between Allow and Block states.
 *
 * Used in App Inventory targeting to determine whether selected items
 * should be allowed or blocked.
 *
 * @example
 * ```tsx
 * <AllowBlockToggle
 *   value={AllowBlockType.ALLOW}
 *   onChange={(newValue) => setAllowBlockType(newValue)}
 * />
 * ```
 */
export const AllowBlockToggle = ({
  value,
  onChange,
  disabled = false,
  disableAllow = false,
  dataTest = 'allow-block-toggle',
}: AllowBlockToggleProps) => {
  const { t } = useTranslation();

  const handleChange = (_event: React.MouseEvent<HTMLElement>, newValue: AllowBlockType | null) => {
    // Prevent deselection - always keep one option selected
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  return (
    <ToggleButtonGroupDesignSystemScope>
      <StyledToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        aria-label={t('Allow or Block toggle')}
        disabled={disabled}
        data-test={dataTest}
      >
        <StyledAllowToggleButton
          value={AllowBlockType.ALLOW}
          aria-label={t('Allow')}
          disabled={disableAllow}
          data-test={`${dataTest}-allow`}
        >
          {t('Allow')}
        </StyledAllowToggleButton>
        <StyledBlockToggleButton value={AllowBlockType.BLOCK} aria-label={t('Block')} data-test={`${dataTest}-block`}>
          {t('Block')}
        </StyledBlockToggleButton>
      </StyledToggleButtonGroup>
    </ToggleButtonGroupDesignSystemScope>
  );
};
